import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTh, FaList, FaFilter, FaSearch } from "react-icons/fa";
import "./ItemListHeader.css";

const SORT_FIELDS = [
  { value: "year", label: "Year" },
  { value: "brand", label: "Brand" },
  { value: "set_name", label: "Set" },
  { value: "subset", label: "Subset / Insert" },
  { value: "name", label: "Name" },
];

function ItemListHeader({
  collections,
  selectedCollection,
  setSelectedCollection,
  userCategories,
  selectedCategory,
  setSelectedCategory,
  selectedSubcategory,
  setSelectedSubcategory,
  userSubcategories = [],
  setShowAddCardModal,
  setShowHobbliVision,
  multiSelectMode,
  setMultiSelectMode,
  selectedCards,
  setSelectedCards,
  handleDeleteClick,
  setShowCollectionModal,
  setShowManageCollections,
  searchTerm,
  setSearchTerm,
  sortConfig,
  setSortConfig,
  viewMode,
  setViewMode,
  itemsPerPage,
  setItemsPerPage,
  itemsPerPageOptions = [10, 20, 50],
}) {
  const [showFilterSortModal, setShowFilterSortModal] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [isMobileFilter, setIsMobileFilter] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const handleChange = (event) => setIsMobileFilter(event.matches);
    setIsMobileFilter(media.matches);
    media.addEventListener?.("change", handleChange);
    return () => media.removeEventListener?.("change", handleChange);
  }, []);

  useEffect(() => {
    if (!showFilterSortModal && !showAddMenu) return undefined;

    const previousOverflow = document.body.style.overflow;
    if (showFilterSortModal) document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showFilterSortModal, showAddMenu]);

  const DEFAULT_SECONDARY_SORTS = {
    name: { field: "year", direction: "asc" },
    brand: { field: "year", direction: "asc" },
    set_name: { field: "year", direction: "asc" },
    subset: { field: "year", direction: "asc" },
    estimated_value: { field: "year", direction: "asc" },
  };

  const updateSort = (index, value) => {
    const updated = [...sortConfig];

    updated[index] = {
      ...updated[index],
      field: value,
    };

    if (
      index === 0 &&
      (!updated[1]?.field || updated[1]?.autoGenerated)
    ) {
      const defaultSecondary = DEFAULT_SECONDARY_SORTS[value];

      updated[1] = defaultSecondary
        ? { ...defaultSecondary, autoGenerated: true }
        : { field: "", direction: "asc" };
    }

    setSortConfig(updated);
  };

  const updateDirection = (index, direction) => {
    const updated = [...sortConfig];
    updated[index] = { ...updated[index], direction };
    setSortConfig(updated);
  };

  return (
    <div className="item-list-header">

      {/* HEADER BAR */}
      <div className="item-list-header-bar">

        {/* LEFT: KEEP SIDE-BY-SIDE */}
        <div className="item-list-header-left item-list-filter-group">
          <select
            className="item-list-collection-select"
            aria-label="Filter by collection"
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
          >
            <option value="all">By Collection</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            className="item-list-collection-select"
            aria-label="Filter by category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">By Category</option>
            {userCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <select
            className="item-list-collection-select"
            aria-label="Filter by subcategory"
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
          >
            <option value="all">By Subcategory</option>
            {userSubcategories.map((sub) => (
              <option key={sub.id} value={sub.id}>{sub.name}</option>
            ))}
          </select>
        </div>

        <div className="item-list-header-right">
          {selectedCards.length > 0 ? (
            <>
              <span className="item-list-selection-count">{selectedCards.length} selected</span>
              <button
                className="item-list-btn item-list-btn-collection"
                onClick={() => setShowCollectionModal(true)}
              >
                Add to Collection
              </button>
              <button
                className="item-list-btn item-list-btn-delete"
                onClick={() => handleDeleteClick()}
              >
                Delete
              </button>
              <button
                className="item-list-btn item-list-btn-cancel"
                onClick={() => {
                  setSelectedCards([]);
                  setMultiSelectMode(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <div className="item-list-add-wrapper">
              <button
                className="item-list-btn item-list-btn-add"
                onClick={() => setShowAddMenu(prev => !prev)}
              >
                + Add Item
              </button>

              {showAddMenu && createPortal(
                <div
                  className="item-list-add-backdrop"
                  onClick={() => setShowAddMenu(false)}
                >
                  <div
                    className="item-list-add-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="item-list-add-menu-handle" aria-hidden="true" />

                    <button
                      type="button"
                      className="item-list-add-option"
                      onClick={() => {
                        setShowAddMenu(false);
                        setShowHobbliVision(true);
                      }}
                    >
                      <div className="item-list-add-option-icon scan">📷</div>
                      <div className="item-list-add-option-content">
                        <div className="item-list-add-option-title">Scan Item</div>
                        <div className="item-list-add-option-description">Use HobbliVision to identify it</div>
                      </div>
                      <div className="item-list-add-option-arrow">→</div>
                    </button>

                    <button
                      type="button"
                      className="item-list-add-option"
                      onClick={() => {
                        setShowAddMenu(false);
                        setShowAddCardModal(true);
                      }}
                    >
                      <div className="item-list-add-option-icon manual">✏️</div>
                      <div className="item-list-add-option-content">
                        <div className="item-list-add-option-title">Enter Manually</div>
                        <div className="item-list-add-option-description">Add an item yourself</div>
                      </div>
                      <div className="item-list-add-option-arrow">→</div>
                    </button>
                  </div>
                </div>,
                document.body
              )}

            </div>

              <button
                className={`item-list-btn item-list-btn-multi ${multiSelectMode ? "active" : ""}`}
                onClick={() => {
                  if (multiSelectMode) setSelectedCards([]);
                  setMultiSelectMode(!multiSelectMode);
                }}
              >
                {multiSelectMode ? "Cancel" : "Select Multiple"}
              </button>

              <button
                className="item-list-btn item-list-btn-filter"
                onClick={() => setShowFilterSortModal(true)}
              >
                <FaFilter /> Filter / Sort
              </button>

              <button
                className="item-list-btn item-list-btn-view-toggle"
                title={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                aria-label={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
              >
                {viewMode === "grid" ? <FaList /> : <FaTh />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* FILTER / SORT MODAL */}
      {showFilterSortModal && createPortal(
        <div
          className="item-list-filter-backdrop"
          onClick={() => setShowFilterSortModal(false)}
        >
          <div
            className="item-list-filter-modal"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="item-list-filter-modal-header">
              <div>
                <h3>Filter & Sort</h3>
                <p>Refine your collection and choose how items are ordered.</p>
              </div>
              <button
                type="button"
                className="item-list-filter-close"
                aria-label="Close filter and sort"
                onClick={() => setShowFilterSortModal(false)}
              >
                <span className="item-list-filter-close-glyph" aria-hidden="true">×</span>
              </button>
            </div>

            <div className="item-list-filter-modal-body">
              <div className="item-list-filter-grid">
                <div className="item-list-modal-section">
                  <label>Collection</label>
                  <select
                    value={selectedCollection}
                    onChange={(e) => setSelectedCollection(e.target.value)}
                  >
                    <option value="all">All Items</option>
                    {collections.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="item-list-modal-section">
                  <label>Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {userCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="item-list-modal-section">
                  <label>Subcategory</label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                  >
                    <option value="all">All Subcategories</option>
                    {userSubcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="item-list-modal-section item-list-search-section">
                <label>Search</label>
                <div className="item-list-search-wrap">
                  <FaSearch aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Search Items"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="item-list-sort-panel">
                <div className="item-list-sort-heading">
                  <span>Sort order</span>
                  <span className="item-list-sort-hint">Up to 3 levels</span>
                </div>

                {[0, 1, 2]
                  .filter((index) =>
                    !isMobileFilter ||
                    index === 0 ||
                    (index === 1 && Boolean(sortConfig[0]?.field)) ||
                    (index === 2 && Boolean(sortConfig[1]?.field))
                  )
                  .map((index) => (
                  <div key={index} className="item-list-sort-row">
                    <span className="item-list-sort-step">{index === 0 ? "Sort by" : "Then by"}</span>
                    <select
                      aria-label={index === 0 ? "Primary sort field" : `Sort field ${index + 1}`}
                      value={sortConfig[index]?.field || ""}
                      onChange={(e) => updateSort(index, e.target.value)}
                    >
                      <option value="">Choose field...</option>
                      {SORT_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <select
                      aria-label={`Sort direction ${index + 1}`}
                      value={sortConfig[index]?.direction || "asc"}
                      onChange={(e) => updateDirection(index, e.target.value)}
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                  </div>
                ))}
              </div>

              <div className="item-list-page-size-row">
                <div>
                  <label>Items per page</label>
                  <span>Choose how many items to show at once.</span>
                </div>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  {itemsPerPageOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* FIXED FOOTER */}
            <div className="item-list-filter-modal-footer">

              {collections.length > 0 &&
                selectedCollection !== "all" && (
                  <button
                    className="item-list-btn item-list-btn-manage"
                    onClick={() => setShowManageCollections(true)}
                  >
                    Manage Collections
                  </button>
                )}

              <button
                className="item-list-btn item-list-btn-done"
                onClick={() => setShowFilterSortModal(false)}
              >
                Done
              </button>

            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ItemListHeader;